// Print + PDF helpers. Both paths take a pre-built page SVG (from
// print-layout.buildPageSvg) so the popup-print output and the PDF
// download stay byte-for-byte identical.

export type PrintPopupOptions = {
  title: string;
  /** Full page SVG, including <?xml?> prolog and explicit mm dimensions. */
  pageSvg: string;
};

/**
 * Open a popup window containing only the supplied page SVG, then fire the
 * browser print dialog. Must be invoked from a user-gesture handler so the
 * popup isn't blocked.
 */
export function printPageSvg({ title, pageSvg }: PrintPopupOptions): void {
  if (typeof window === "undefined") return;
  const win = window.open("", "_blank");
  if (!win) {
    window.alert("Please allow popups for this site to print barcodes.");
    return;
  }

  const escapedTitle = escapeHtml(title);
  const svg = pageSvg.replace(/^<\?xml[^?]*\?>\s*/, "");

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapedTitle}</title>
<style>
  @page { margin: 0; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { display: flex; align-items: center; justify-content: center; }
  svg { display: block; max-width: 100%; height: auto; }
  @media print {
    body { display: block; }
    svg { max-width: none; }
  }
</style>
</head>
<body>
${svg}
<script>
  window.addEventListener("load", function () {
    setTimeout(function () { try { window.focus(); } catch (_) {} window.print(); }, 50);
  });
  window.addEventListener("afterprint", function () { window.close(); });
</script>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

export type PdfDownloadOptions = {
  /** Filename without the .pdf extension. */
  filename: string;
  /** Full page SVG with mm dimensions. */
  pageSvg: string;
};

/** Convert the page SVG to a PDF blob via jsPDF + svg2pdf.js and trigger a
 *  download. Uses dynamic imports so the heavy PDF chunk only loads when the
 *  user actually asks for a PDF. */
export async function downloadPdfFromPageSvg({
  filename,
  pageSvg,
}: PdfDownloadOptions): Promise<void> {
  if (typeof window === "undefined") return;
  // Parse the page SVG to recover paper dimensions in mm.
  const parser = new DOMParser();
  const doc = parser.parseFromString(pageSvg, "image/svg+xml");
  const svgEl = doc.documentElement as unknown as SVGSVGElement;
  const widthMm = parseFloat(svgEl.getAttribute("width") || "0") || 210;
  const heightMm = parseFloat(svgEl.getAttribute("height") || "0") || 297;

  const [{ default: jsPDFCtor }, svg2pdfMod] = await Promise.all([
    import("jspdf"),
    import("svg2pdf.js"),
  ]);
  const svg2pdfFn = (svg2pdfMod as unknown as { svg2pdf: typeof import("svg2pdf.js").svg2pdf })
    .svg2pdf;

  const pdf = new jsPDFCtor({
    orientation: widthMm > heightMm ? "landscape" : "portrait",
    unit: "mm",
    format: [widthMm, heightMm],
  });

  // svg2pdf walks SVG nodes and emits vector PDF primitives — keeps the
  // barcode crisp at any zoom level instead of rasterising.
  await svg2pdfFn(svgEl, pdf, { x: 0, y: 0, width: widthMm, height: heightMm });

  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
